<template>
  <div class="tw-w-3/5">
    <Header
      :title="isEditMode ? $t('Edit job brief') : $t('Create a job brief')"
      :link-text="$t('All jobs')"
      :link-url="laravelNamedRoute(namedRoutes.frontend.jobs.index.created)"
    >
      <template #right />
    </Header>

    <SourceLinkBanner
      v-if="sourceJob"
      class="tw-mt-6"
      :message="sourceJobBannerMessage"
      :subtitle="sourceJobBannerSubtitle"
      :avatar-url="sourceJob.company?.avatar ?? undefined"
      :details="sourceJobBannerDetails"
    />

    <WdsStack direction-column gap-custom="2.5rem" class="tw-mt-8">
      <form @submit.prevent="onSubmit">
        <WdsStack direction-column gap-custom="2.5rem">
          <BaseSection>
            <BaseSectionHeader :header-text="$t('Overview')" />

            <BaseSectionBody>
              <BaseFieldGroup :label="$t('Title')">
                <div class="tw-w-full">
                  <WdsInput
                    v-model="title"
                    class="tw-w-full"
                    :placeholder="$t('Senior Front-End Engineer')"
                    data-test="brief-title"
                    required
                  />
                </div>
              </BaseFieldGroup>

              <BaseFieldGroup
                :label="$t('Job owners')"
                :helper-text="$t('Who is responsible for hiring this job?')"
              >
                <div class="tw-w-full">
                  <WdsAvatarGroup
                    sm
                    :max-avatars="15"
                    class="tw-float-right tw-mb-sm"
                    :class="{ 'tw-invisible': jobOwnersAvatars.length === 0 }"
                    :avatars="jobOwnersAvatars.length ? jobOwnersAvatars : [{ path: '' }]"
                  />

                  <WdsSelect
                    v-model="jobOwners"
                    multi-select
                    :list="teamMembersOptions"
                    :placeholder="$t('Add/remove owners...')"
                    prefix-icon="search"
                    searchable
                    class="tw-w-full"
                    data-test="brief-owners"
                  >
                    <template #option="{ option }">
                      <WdsStack align-center gap-sm>
                        <WdsAvatar xs :path="getAvatarPath(option.avatar, option.value)" />
                        <div>{{ option.label }}</div>
                      </WdsStack>
                    </template>
                    <template #header="{ options }">
                      <span v-if="options.length">
                        {{ options.length + ' selected' }}
                      </span>
                      <span v-else>
                        {{ $t('Add/remove owners...') }}
                      </span>
                    </template>
                  </WdsSelect>
                </div>
              </BaseFieldGroup>

              <BaseFieldGroup :label="$t('Description')" full-width>
                <template #label>
                  <WdsStack direction-row class="tw-w-full tw-justify-between">
                    <WdsText primary large>{{ $t('Description') }}</WdsText>

                    <JobDescriptionGenerator
                      v-if="aiConsent"
                      :company-id="accountId"
                      :context="AiJobDescriptionContext.JobPost"
                      :job-id="jobId"
                      :title="title"
                      :description="description"
                      @generate-description="handleGeneratedDescription"
                    />
                  </WdsStack>
                  <WdsStack v-if="aiDescriptionUsed" direction-column class="tw-w-full tw-py-2">
                    <WdsAlert warning icon>
                      {{
                        $t(
                          'AI draft — please review for accuracy, fairness, and legal compliance before using.',
                        )
                      }}
                    </WdsAlert>
                  </WdsStack>
                </template>

                <template #helperText>
                  <WdsText secondary class="tw-mb-2">
                    {{
                      $t(
                        'Share what your supplier needs to take it from here — candidate, role, and any supplier or agency context.',
                      )
                    }}
                  </WdsText>
                </template>
                <div class="tw-w-full">
                  <WdsInputArea
                    v-model="description"
                    :placeholder="$t('Enter description here')"
                    data-test="brief-description"
                    height="160px"
                    required
                  />
                </div>
              </BaseFieldGroup>
            </BaseSectionBody>
          </BaseSection>

          <BaseSection v-if="showContactDetails">
            <BaseSectionHeader :header-text="$t('Contact details')" />

            <BaseSectionBody>
              <WdsStack v-if="contactProfileIncomplete" direction-column class="tw-w-full tw-pb-2">
                <WdsAlert info icon bordered>
                  {{
                    $t(
                      'Complete your profile so your name, email and phone number fill in here automatically.',
                    )
                  }}
                  <WdsButtonText :href="profileEditUrl">{{ $t('Update your profile') }}</WdsButtonText>
                </WdsAlert>
              </WdsStack>

              <BaseFieldGroup :label="$t('Contact name')" optional>
                <div class="tw-w-full">
                  <WdsInput
                    v-model="contactName"
                    class="tw-w-full"
                    :placeholder="$t('Full name')"
                    data-test="brief-contact-name"
                  />
                </div>
              </BaseFieldGroup>

              <BaseFieldGroup :label="$t('Contact email')" optional>
                <div class="tw-w-full">
                  <WdsInput
                    v-model="contactEmail"
                    class="tw-w-full"
                    :placeholder="$t('name@company.com')"
                    data-test="brief-contact-email"
                  />
                </div>
              </BaseFieldGroup>

              <BaseFieldGroup :label="$t('Contact phone')" optional>
                <div class="tw-w-full">
                  <InputTelephone
                    :key="`brief-contact-phone-${phoneSeedKey}`"
                    :persistent-phone-number="contactPhone"
                    :show-verify-button="false"
                    :placeholder="$t('+45 12 34 56 78')"
                    data-test="brief-contact-phone"
                    @phone-changed="(value) => (contactPhone = value ?? '')"
                  />
                </div>
              </BaseFieldGroup>
            </BaseSectionBody>
          </BaseSection>

          <BaseSection>
            <BaseSectionHeader
              :header-text="$t('Timeframe')"
              :sub-text="$t('This can be adjusted before the hire offer.')"
            />

            <BaseSectionBody>
              <BaseFieldGroup :label="$t('Start date')">
                <div class="tw-w-full">
                  <InputDatetimePicker
                    v-model="startDate"
                    format="YYYY-MM-DD"
                    :placeholder="$t('Select a start date')"
                    data-test="brief-start-date"
                    required
                  />
                </div>
              </BaseFieldGroup>

              <BaseFieldGroup :label="$t('End date')">
                <div class="tw-w-full">
                  <InputDatetimePicker
                    v-model="endDate"
                    format="YYYY-MM-DD"
                    :placeholder="$t('Select an end date')"
                    :min-date="startDate"
                    data-test="brief-end-date"
                    required
                  />
                </div>
              </BaseFieldGroup>
            </BaseSectionBody>
          </BaseSection>

          <BaseSection>
            <BaseSectionHeader
              :header-text="$t('Rate')"
              :sub-text="$t('This can be adjusted before the hire offer.')"
            />

            <BaseSectionBody>
              <BaseFieldGroup
                :label="$t('Payment')"
                :helper-text="$t('How would you prefer to pay for this job?')"
              >
                <div class="tw-w-full">
                  <WdsSelect
                    v-model="rateTypeModel"
                    :list="rateOptions"
                    data-test="brief-rate-type-dropdown"
                  />
                </div>
              </BaseFieldGroup>

              <BaseFieldGroup :label="$t('Rate')">
                <div class="tw-w-full">
                  <WdsInputCurrency
                    v-model="rate"
                    :config="inputCurrencyConfig"
                    data-test="brief-rate"
                    required
                    class="tw-w-full"
                  />
                </div>
              </BaseFieldGroup>
            </BaseSectionBody>
          </BaseSection>

          <BaseSection v-show="companyFields.length > 0">
            <BaseSectionHeader :header-text="$t('Custom fields')" />
            <BaseSectionBody>
              <FieldableFormCustomFields
                v-model:field-values="customFieldValues"
                :fieldable-type="TypeSupportingCustomFieldValues.Job"
                :fieldable-id="jobId"
                :account-id="accountId"
                :should-display-errors="hasBeenTouched"
              />
            </BaseSectionBody>
          </BaseSection>

          <BaseSection v-if="singleSupplierId && supplierFields.length > 0">
            <BaseSectionHeader
              :header-text="
                singleSupplierName
                  ? $t(':name required fields', { name: singleSupplierName })
                  : $t('Supplier required fields')
              "
            />
            <BaseSectionBody>
              <SupplierSharedFieldsForm
                v-model:field-values="customFieldValues"
                :supplier-id="singleSupplierId"
                :fieldable-type="TypeSupportingCustomFieldValues.Job"
                :fieldable-id="jobId"
                :account-id="accountId"
                :should-display-errors="hasBeenTouched"
              />
            </BaseSectionBody>
          </BaseSection>

          <WdsStack align-center justify-end gap-md>
            <WdsSpinner v-if="saving" indeterminate data-test="brief-saving-spinner" />

            <WdsButton data-test="brief-submit" primary :disabled="saving" @click="onSubmit">
              {{ isEditMode ? $t('Save changes') : $t('Create job brief') }}
            </WdsButton>
          </WdsStack>
        </WdsStack>
      </form>
    </WdsStack>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, toRef, watch } from 'vue'
import moment from 'moment'
import {
  WdsAlert,
  WdsAvatar,
  WdsAvatarGroup,
  WdsButton,
  WdsButtonText,
  WdsInput,
  WdsInputArea,
  WdsInputCurrency,
  WdsSelect,
  WdsSpinner,
  WdsStack,
  WdsText,
} from '@worksome/wds-components'
import { useMutation, useQuery } from '@vue/apollo-composable'
import axios from 'axios'
import { helpers, required } from '@vuelidate/validators'
import useVuelidate from '@vuelidate/core'

import { $t } from '@/composables/translation'
import BaseSection from '@/components/BaseSection.vue'
import BaseSectionHeader from '@/components/BaseSectionHeader.vue'
import BaseSectionBody from '@/components/BaseSectionBody.vue'
import BaseFieldGroup from '@/components/BaseFieldGroup.vue'
import InputDatetimePicker from '@/components/InputDatetimePicker.vue'
import InputTelephone from '@/components/InputTelephone.vue'
import FieldableFormCustomFields from '@/components/Fieldable/FieldableFormCustomFields.vue'
import SupplierSharedFieldsForm from '@/components/Fieldable/SupplierSharedFieldsForm.vue'
import useFieldableCustomFields from '@/components/Fieldable/composables/useFieldableCustomFields'
import useSupplierSharedCustomFields from '@/components/Fieldable/composables/useSupplierSharedCustomFields'
import { useFieldableFormStore } from '@/components/Fieldable/store/useFieldableFormStore'
import { PER_PAGE_MAXIMUM } from '@/utils/constants'
import {
  diffCustomFieldValueInputs,
  mapCustomFieldValueToCustomFieldValueInput,
} from '@/components/Fieldable/utils'
import { laravelNamedRoute } from '@/utils/laravelNamedRoutes'
import namedRoutes from '@/utils/laravelNamedRoutes.json'
import { NetworkError } from '@/utils/notification'
import { FormValidation } from '@/utils/formValidation'

import Header from '@/components/Job/Header.vue'
import SourceLinkBanner from '@/components/SourceLinkBanner.vue'
import JobDescriptionGenerator from '@/components/Job/Edit/Tabs/OverviewTab/JobDescriptionGenerator.vue'
import { UpdateJobMutation } from '@/components/Job/graphql/mutations'
import {
  CompanyQuery,
  GetJobFormQuery,
  TeamMembersAndOwnersQuery,
} from '@/components/Job/graphql/queries'
import { getAvatarPath } from '@/utils/avatar'
import { JobFormDataFragment } from '@/components/Job/graphql/fragments'
import { useFragment } from '@/gql'
import {
  AiJobDescriptionContext,
  CustomFieldTypeValueInput,
  JobEndTimeframe,
  JobStartTimeframe,
  RateType,
  TypeSupportingCustomFieldValues,
} from '@/gql/graphql'

const props = defineProps({
  jobId: {
    type: String,
    required: true,
  },
  accountId: {
    type: String,
    required: true,
  },
  // Set when the company has exactly one supplier; gates the supplier-shared
  // custom fields section, mirroring the full job-edit flow.
  singleSupplierId: {
    type: String,
    default: undefined,
  },
  singleSupplierName: {
    type: String,
    default: undefined,
  },
  showContactDetails: {
    type: Boolean,
    default: false,
  },
  prefillContactName: {
    type: String,
    default: '',
  },
  prefillContactEmail: {
    type: String,
    default: '',
  },
  prefillContactPhone: {
    type: String,
    default: '',
  },
})

const fieldableStore = useFieldableFormStore()

// Both field sets are fetched here only to gate their sections; the form
// components fetch the same (deduped) queries for rendering. The shared
// fieldable store can't gate either section — each component overwrites it.
const { fields: companyFields } = useFieldableCustomFields(
  props.accountId,
  [props.accountId],
  [TypeSupportingCustomFieldValues.Job],
  PER_PAGE_MAXIMUM,
  true,
)

const { fields: supplierFields } = useSupplierSharedCustomFields(
  props.accountId,
  toRef(props, 'singleSupplierId'),
  [TypeSupportingCustomFieldValues.Job],
)

// Form state
const title = ref<string>('')
const description = ref<string>('')
const contactName = ref<string>('')
const contactEmail = ref<string>('')
const contactPhone = ref<string>('')
const phoneSeedKey = ref<number>(0)
const startDate = ref<string>('')
const endDate = ref<string>('')

// WdsInputCurrency v-models a string; we coerce to Number on submit.
// Empty string represents the unfilled state (vuelidate `required` rejects it).
const rate = ref<string>('')
const rateTypeValue = ref<RateType>(RateType.Hourly)
const customFieldValues = ref<CustomFieldTypeValueInput[]>([])
const jobOwners = ref<{ value: string; avatar: string | null; label?: string }[]>([])
const saving = ref<boolean>(false)
const hasBeenTouched = ref<boolean>(false)
const aiDescriptionUsed = ref<boolean>(false)

// `is_published` is flipped to true on first submit (via briefPublish), so
// we use it to distinguish "creating for the first time" from "editing an
// existing brief". `is_available` isn't usable here — it defaults to true
// on a fresh Yjob.
const isEditMode = ref<boolean>(false)

// Rate type select wraps a { value } object, matching the pattern in BudgetTab.
const rateTypeModel = computed({
  get: () => ({ value: rateTypeValue.value }),
  set: (selected: { value: RateType }) => {
    rateTypeValue.value = selected.value
  },
})

const rateOptions = [
  { value: RateType.Hourly, label: $t('Hourly Rate') },
  { value: RateType.Daily, label: $t('Daily Rate') },
  { value: RateType.Weekly, label: $t('Weekly Rate') },
  { value: RateType.Monthly, label: $t('Monthly Rate') },
  { value: RateType.Fixed, label: $t('Fixed Amount') },
]

// Company + currency (for rate input formatting)
const { result: companyResult } = useQuery(
  CompanyQuery,
  { companyId: props.accountId },
  { clientId: 'publicApi', fetchPolicy: 'no-cache' },
)

// @ts-ignore - window.currentLocale is injected globally
const { thousandsSeparator, decimalSeparator } = window.currentLocale

const inputCurrencyConfig = computed(() => ({
  currencySymbol: companyResult.value?.company?.currency || 'USD',
  thousandsSeparator,
  decimalSeparator,
  showFractions: true,
}))

const { result: teamMembersResult } = useQuery(
  TeamMembersAndOwnersQuery,
  { companyId: props.accountId, first: 100 },
  { clientId: 'publicApi', fetchPolicy: 'no-cache' },
)

const teamMembersOptions = computed(() =>
  (teamMembersResult.value?.company?.teamMembersAndOwners?.data ?? []).map((member) => ({
    value: member.id,
    label: member.name,
    avatar: member.avatar,
  })),
)

const jobOwnersAvatars = computed(() =>
  jobOwners.value.map((owner) => ({
    path: getAvatarPath(owner.avatar, owner.value),
  })),
)

// Load the persisted job so we can pre-fill the title the user entered in
// step 1. Any other previously-saved fields (e.g. if the user revisits the
// brief edit page later) will be populated here too.
const { result: jobResult } = useQuery(
  GetJobFormQuery,
  { jobId: props.jobId },
  { clientId: 'publicApi', fetchPolicy: 'no-cache' },
)

watch(jobResult, (value) => {
  if (!value?.job) return
  const job = useFragment(JobFormDataFragment, value.job)

  title.value = job.name ?? ''
  description.value = job.description ?? ''

  contactName.value = job.contactName ?? props.prefillContactName ?? ''
  contactEmail.value = job.contactEmail ?? props.prefillContactEmail ?? ''
  contactPhone.value = job.contactPhone ?? props.prefillContactPhone ?? ''
  // Remount the telephone input so it seeds from the loaded/pre-filled value
  // (InputTelephone only reads persistent-phone-number on created()).
  phoneSeedKey.value += 1
  startDate.value = job.startDate ?? ''
  endDate.value = job.endDate ?? ''

  // WdsInputCurrency is a text-formatted input that reads/writes strings.
  if (job.rate !== null && job.rate !== undefined) {
    rate.value = job.rate.toString()
  }
  if (job.rateType) {
    rateTypeValue.value = job.rateType
  }
  if (job.customFieldValues?.length) {
    customFieldValues.value = mapCustomFieldValueToCustomFieldValueInput(job.customFieldValues)
  }
  if (job.owners?.length) {
    jobOwners.value = job.owners.map((owner) => ({
      value: owner.id,
      label: owner.name,
      avatar: owner.avatar ?? null,
    }))
  }

  isEditMode.value = job.published === true
})

// Pristine baseline of the job's stored custom field values, kept apart from
// the editable ref the two custom-field forms write into.
const initialCustomFieldValueInputs = computed<CustomFieldTypeValueInput[]>(() => {
  const job = jobResult.value?.job ? useFragment(JobFormDataFragment, jobResult.value.job) : null

  return job?.customFieldValues?.length
    ? mapCustomFieldValueToCustomFieldValueInput(job.customFieldValues)
    : []
})

// AI consent uses the globally-injected currentAccount, matching the
// existing pattern in OverviewTab.
// @ts-ignore - currentAccount is injected globally
const aiConsent = computed(() => currentAccount?.settings?.ai_consent ?? false)

// Client-side job this brief mirrors (supplier-side view), unmasked
// straight from the form fragment. Drives the header banner below.
const sourceJob = computed(() =>
  jobResult.value?.job
    ? (useFragment(JobFormDataFragment, jobResult.value.job).sourceJob ?? null)
    : null,
)

// Banner: brief mirrors a client job (supplier-side view).
const sourceJobBannerMessage = computed(() =>
  sourceJob.value?.company?.name
    ? `${$t('Brief from')} ${sourceJob.value.company.name}`
    : $t('Brief from client'),
)

const sourceJobBannerSubtitle = computed(() => {
  const jobName = sourceJob.value?.name
  return jobName
    ? `${$t('Your supplier version of')} "${jobName}"`
    : $t("Your supplier version of the client's brief")
})

const formatBriefPeriod = (start: string, end: string | null): string => {
  const s = moment(start)
  if (!end) return s.format('MMM D, YYYY')
  const e = moment(end)
  // Collapse the year when start and end share one, e.g. "May 11 – Aug 29, 2026"
  return s.isSame(e, 'year')
    ? `${s.format('MMM D')} – ${e.format('MMM D, YYYY')}`
    : `${s.format('MMM D, YYYY')} – ${e.format('MMM D, YYYY')}`
}

const sourceJobBannerDetails = computed<string[]>(() => {
  const details: string[] = []
  const src = sourceJob.value
  if (!src) return details

  if (src.rate !== null && src.currency) {
    const rateTypeLabels: Record<string, string> = {
      hourly: $t('hour'),
      daily: $t('day'),
      weekly: $t('week'),
      monthly: $t('month'),
      fixed: $t('fixed'),
    }
    const label = src.rateType ? rateTypeLabels[String(src.rateType).toLowerCase()] : undefined
    const suffix = label ? ` / ${label}` : ''
    details.push(`${$t('Client rate')}: ${src.currency} ${src.rate}${suffix}`)
  }
  if (src.startDate) {
    details.push(`${$t('Period')}: ${formatBriefPeriod(src.startDate, src.endDate)}`)
  }
  return details
})

const { mutate: mutateUpdateJob } = useMutation(UpdateJobMutation, {
  clientId: 'publicApi',
})

const handleGeneratedDescription = (generated: string) => {
  description.value = generated
  aiDescriptionUsed.value = true
}

// Vuelidate for the non-custom fields. Custom fields have their own
// fieldable store which honours `is_required` for each field definition.
const contactProfileIncomplete = computed(
  () => !props.prefillContactName || !props.prefillContactEmail || !props.prefillContactPhone,
)

const profileEditUrl = laravelNamedRoute(namedRoutes.frontend.user.profile['edit-user'])

const v$ = useVuelidate(
  {
    title: { required },
    description: { required },
    startDate: { required },
    endDate: { required },
    rate: { required },
    // Contact details are optional on the brief form; only validate format if given.
    contactEmail: {
      email: helpers.withMessage($t('Please enter a valid email address'), (value: unknown) => {
        const trimmed = String(value ?? '').trim()
        return !trimmed || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)
      }),
    },
  },
  { title, description, startDate, endDate, rate, contactEmail },
)

const onSubmit = async () => {
  hasBeenTouched.value = true
  v$.value.$touch()
  // The fieldable store validator is not auto-dirty; without this touch its
  // errors stay empty and required custom fields never block the submit.
  fieldableStore.v$.$touch()

  const isValid = await v$.value.$validate()
  const hasCustomFieldErrors = Object.keys(fieldableStore.errors ?? {}).length > 0

  if (!isValid || hasCustomFieldErrors) return

  // Only the custom field values the user changed since the brief was loaded.
  // Re-submitting untouched fields deletes their stored values whenever the
  // form holds them as empty, so an unchanged form must leave the key out.
  const changedCustomFieldValues = diffCustomFieldValueInputs(
    customFieldValues.value,
    initialCustomFieldValueInputs.value,
  )

  try {
    saving.value = true

    await mutateUpdateJob({
      input: {
        id: props.jobId,
        name: title.value,
        description: description.value,
        contactName: contactName.value,
        contactEmail: contactEmail.value,
        contactPhone: contactPhone.value,
        startDate: startDate.value,
        startDateTimeframe: JobStartTimeframe.OnDate,
        endDate: endDate.value,
        endDateTimeframe: JobEndTimeframe.OnDate,
        rateType: {
          type: rateTypeValue.value,
          rate: rate.value === '' ? null : Number(rate.value),
        },
        owners: jobOwners.value.map(({ value }) => value),
        ...(changedCustomFieldValues.length > 0
          ? { customFieldValues: changedCustomFieldValues }
          : {}),
      },
    })

    // Flip is_created/is_available/is_published so the brief can be shared
    // with the MSP. Idempotent — safe to call on every save.
    await axios.post(
      laravelNamedRoute(namedRoutes.frontend.flows.hiring['brief-publish'], {
        yjob: props.jobId,
      }),
    )

    window.location.href = laravelNamedRoute(namedRoutes.jobs.candidates, {
      job: props.jobId,
    })
  } catch (ex: any) {
    // Surface the server's validation messages (e.g. which required custom
    // fields are missing) instead of a generic error.
    const validationMessages = Object.values(
      FormValidation.fromGraphqlErrors(ex?.graphQLErrors) ?? {},
    ).flat()

    new NetworkError({
      ex: validationMessages.length ? false : ex,
      msg: validationMessages.join(' ') || $t('Something went wrong when saving your job brief.'),
    }).dispatch()

    saving.value = false
  }
}
</script>

<style scoped lang="scss">
:deep(.wds-input-label) {
  @apply tw-mb-0;
}
</style>
